import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "backdrop", "panel", "form",
    "searchInput", "list",
    "countryRow",
    "regionChip",
    "counter",
    "showAllToggle",
    "applyButton",
    "emptyMessage",
    "pinnedSeparator"
  ]

  static values = {
    sessionCountryIds: Array,
    totalCount: Number,
    homeCountryId: Number
  }


  connect() {
    this.selected   = new Set(this.sessionCountryIdsValue.map(String))
    this.committed  = new Set(this.selected)
    this.filterMode = "all"   // "all" | "selected"
    this.searchTerm = ""
    this.holdsScrollLock = false
    this.boundTrapFocus = this.trapFocus.bind(this)

    this.originalOrder = Array.from(this.countryRowTargets)

    this.continentMap = new Map()
    this.countryRowTargets.forEach(row => {
      const id        = row.dataset.countryId
      const continent = row.dataset.continent
      if (!continent) return
      if (!this.continentMap.has(continent)) this.continentMap.set(continent, new Set())
      this.continentMap.get(continent).add(id)
    })

    this.renderAll()

    if (this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")) {
      this.lockScroll()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundTrapFocus)
    if (this.holdsScrollLock) this.unlockScroll()
  }


  toggleRow(event) {
    if (event.type === "keydown" && event.key !== " " && event.key !== "Enter") return
    if (event.type === "keydown") event.preventDefault()

    const row = event.currentTarget
    const id  = row.dataset.countryId
    if (!id) return

    if (this.selected.has(id)) {
      this.selected.delete(id)
    } else {
      this.selected.add(id)
    }

    this.renderRow(row)
    this.updateCounter()
    this.updateChipStates()
    this.updateApplyState()
    this.updateRowVisibility()
  }


  toggleRegion(event) {
    const btn       = event.currentTarget
    const continent = btn.dataset.continent
    const ids       = this.continentMap.get(continent)
    if (!ids || ids.size === 0) return

    const allSelected = [...ids].every(id => this.selected.has(id))
    ids.forEach(id => {
      if (allSelected) this.selected.delete(id)
      else             this.selected.add(id)
    })

    this.renderAll()
  }


  search(event) {
    this.searchTerm = event.target.value.toLowerCase().trim()
    this.renderRowVisibility()
  }

  preventEnter(event) {
    event.preventDefault()
  }


  showAll() {
    this.filterMode = "all"
    this.renderRowVisibility()
  }

  selectedOnly() {
    this.filterMode = "selected"
    this.renderRowVisibility()
  }


  selectAll() {
    this.countryRowTargets.forEach(row => this.selected.add(row.dataset.countryId))
    this.renderAll()
  }

  clearAll() {
    this.selected.clear()
    this.renderAll()
  }


  apply() {
    if (this.selected.size === 0) return
    this.populateHiddenInputs()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  validate(event) {
    if (this.selected.size === 0) {
      event.preventDefault()
      return
    }
    this.populateHiddenInputs()
    this.committed = new Set(this.selected)
  }

  cancel() {
    this.selected   = new Set(this.committed)
    this.searchTerm = ""
    this.filterMode = "all"
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasShowAllToggleTarget) this.showAllToggleTarget.checked = true
    this.renderAll()
    this.hideModal()
  }

  closeOnEscape(event) {
    if (this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")) {
      event.preventDefault()
      this.cancel()
    }
  }

  toggle() {
    if (!this.hasPanelTarget) return
    if (this.panelTarget.classList.contains("hidden")) this.showModal()
    else this.cancel()
  }

  showModal() {
    if (!this.hasPanelTarget) return
    this.savedFocus = document.activeElement
    this.panelTarget.classList.remove("hidden")
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    this.positionPanel()
    if (this.isMobile()) this.lockScroll()
    document.addEventListener("keydown", this.boundTrapFocus)
    requestAnimationFrame(() => {
      if (this.hasSearchInputTarget) this.searchInputTarget.focus()
    })
  }

  hideModal() {
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    this.unlockScroll()
    this.clearPanelPosition()
    document.removeEventListener("keydown", this.boundTrapFocus)
    if (this.savedFocus) this.savedFocus.focus()
  }

  isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches
  }

  positionPanel() {
    if (this.isMobile()) return
    if (!this.savedFocus) return
    const r = this.savedFocus.getBoundingClientRect()
    this.panelTarget.style.top    = `${r.bottom + 4}px`
    this.panelTarget.style.left   = `${r.left}px`
    this.panelTarget.style.right  = "auto"
    this.panelTarget.style.bottom = "auto"
  }

  clearPanelPosition() {
    if (!this.hasPanelTarget) return
    this.panelTarget.style.top    = ""
    this.panelTarget.style.right  = ""
    this.panelTarget.style.left   = ""
    this.panelTarget.style.bottom = ""
  }

  trapFocus(event) {
    if (event.key !== "Tab") return
    if (!this.hasPanelTarget || this.panelTarget.classList.contains("hidden")) return
    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return
    const first = focusable[0]
    const last  = focusable[focusable.length - 1]
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  getFocusableElements() {
    if (!this.hasPanelTarget) return []
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type=\"hidden\"])",
      "[tabindex]:not([tabindex=\"-1\"])",
    ].join(", ")
    return Array.from(this.panelTarget.querySelectorAll(selector)).filter(el => el.offsetParent !== null)
  }


  scoreRow(row, term) {
    const name = row.dataset.name || ""
    const code = row.dataset.code || ""
    const searchData = row.dataset.search || ""

    if (name === term) return 5
    if (name.startsWith(term)) return 4
    if (name.includes(term)) return 3
    if (code === term || code.startsWith(term)) return 2
    if (searchData.includes(term)) return 1
    return 0
  }

  isPinnedRow(row) {
    return row.dataset.home === "true" || row.dataset.detected === "true"
  }

  restoreOriginalOrder() {
    const refNode = this.hasEmptyMessageTarget ? this.emptyMessageTarget : null
    const fragment = document.createDocumentFragment()
    const pinned = []
    const rest = []
    this.originalOrder.forEach(row => {
      if (this.isPinnedRow(row)) pinned.push(row)
      else rest.push(row)
    })
    pinned.forEach(row => fragment.appendChild(row))
    if (this.hasPinnedSeparatorTarget && pinned.length > 0) {
      fragment.appendChild(this.pinnedSeparatorTarget)
    }
    rest.forEach(row => fragment.appendChild(row))
    this.listTarget.insertBefore(fragment, refNode)
  }

  renderAll() {
    this.countryRowTargets.forEach(row => this.renderRow(row))
    this.renderRowVisibility()
    this.updateCounter()
    this.updateChipStates()
    this.updateApplyState()
  }

  renderRow(row) {
    const id       = row.dataset.countryId
    const selected = this.selected.has(id)

    row.setAttribute("aria-checked", selected)

    row.classList.toggle("bg-trust/15",        selected)
    row.classList.toggle("border-trust",       selected)
    row.classList.toggle("border-transparent", !selected)

    const nameEl = row.querySelector("[data-name]")
    if (nameEl) {
      nameEl.classList.toggle("text-surface-100",  selected)
      nameEl.classList.toggle("font-semibold",     selected)
      nameEl.classList.toggle("text-graphite-300", !selected)
    }

    const markEl = row.querySelector("[data-mark]")
    if (markEl) {
      markEl.classList.toggle("bg-trust",      selected)
      markEl.classList.toggle("border-trust",  selected)
      markEl.classList.toggle("border-white/30", !selected)
      const tick = markEl.querySelector("svg")
      if (tick) tick.classList.toggle("hidden", !selected)
    }
  }

  renderRowVisibility(reorder = true) {
    let visibleCount = 0
    const sortable = []
    const visiblePinned = []

    this.countryRowTargets.forEach(row => {
      const isPinned   = this.isPinnedRow(row)
      const id         = row.dataset.countryId
      const searchData = row.dataset.search || ""
      const selected   = this.selected.has(id)

      let visible = true

      if (this.searchTerm && !searchData.includes(this.searchTerm)) {
        visible = false
      }

      if (this.filterMode === "selected" && !selected) {
        visible = false
      }

      // Pinned rows stay visible when there's no search term
      if (isPinned && !this.searchTerm && this.filterMode !== "selected") {
        visible = true
      }

      row.classList.toggle("hidden", !visible)
      if (visible) {
        visibleCount++
        if (isPinned) {
          visiblePinned.push(row)
        } else {
          sortable.push(row)
        }
      }
    })

    if (reorder) {
      if (this.searchTerm) {
        const allVisible = [...visiblePinned, ...sortable]
        allVisible.sort((a, b) => this.scoreRow(b, this.searchTerm) - this.scoreRow(a, this.searchTerm))
        const refNode = this.hasEmptyMessageTarget ? this.emptyMessageTarget : null
        const fragment = document.createDocumentFragment()
        allVisible.forEach(row => fragment.appendChild(row))
        this.listTarget.insertBefore(fragment, refNode)
      } else {
        this.restoreOriginalOrder()
      }
    }

    const nonPinnedRows = this.countryRowTargets.filter(r => !this.isPinnedRow(r))
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.toggle("hidden", visibleCount > 0 || nonPinnedRows.length === 0)
      this.emptyMessageTarget.classList.toggle("flex", visibleCount === 0 && nonPinnedRows.length > 0)
    }

    // Show separator only when there's no active search
    if (this.hasPinnedSeparatorTarget) {
      const showSeparator = !this.searchTerm && visiblePinned.length > 0 && sortable.length > 0
      this.pinnedSeparatorTarget.classList.toggle("hidden", !showSeparator)
    }
  }

  updateRowVisibility() {
    this.renderRowVisibility(false)
  }

  updateCounter() {
    if (!this.hasCounterTarget) return
    this.counterTarget.textContent = `${this.selected.size} of ${this.totalCountValue} selected`
  }

  updateChipStates() {
    if (!this.hasRegionChipTarget) return
    this.regionChipTargets.forEach(chip => {
      const continent = chip.dataset.continent
      const ids       = this.continentMap.get(continent)
      if (!ids || ids.size === 0) {
        chip.setAttribute("aria-pressed", "false")
        this.applyChipStyle(chip, "none")
        return
      }
      let selectedCount = 0
      for (const id of ids) {
        if (this.selected.has(id)) selectedCount++
      }
      if (selectedCount === 0) {
        chip.setAttribute("aria-pressed", "false")
        this.applyChipStyle(chip, "none")
      } else if (selectedCount === ids.size) {
        chip.setAttribute("aria-pressed", "true")
        this.applyChipStyle(chip, "full")
      } else {
        chip.setAttribute("aria-pressed", "mixed")
        this.applyChipStyle(chip, "partial")
      }
    })
  }

  applyChipStyle(chip, state) {
    // Remove all state classes first
    chip.classList.remove(
      "bg-trust", "text-black",
      "border-trust", "text-trust", "bg-trust/10",
      "border-trust/60", "text-trust/70", "bg-trust/5",
      "border-stroke/40", "text-graphite-500"
    )
    if (state === "full") {
      chip.classList.add("bg-trust", "text-black", "border-trust")
    } else if (state === "partial") {
      chip.classList.add("border-trust/60", "text-trust/70", "bg-trust/5")
    } else {
      chip.classList.add("border-stroke/40", "text-graphite-500")
    }
  }

  updateApplyState() {
    if (!this.hasApplyButtonTarget) return
    const empty = this.selected.size === 0
    this.applyButtonTarget.disabled = empty
    this.applyButtonTarget.title    = empty ? "Pick at least one country" : ""
  }


  populateHiddenInputs() {
    if (!this.hasFormTarget) return

    this.formTarget.querySelectorAll("input[name='country_ids[]']").forEach(el => el.remove())

    const fragment = document.createDocumentFragment()
    this.selected.forEach(id => {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = "country_ids[]"
      input.value = id
      fragment.appendChild(input)
    })
    this.formTarget.appendChild(fragment)
  }


  lockScroll() {
    if (this.holdsScrollLock) return
    const el = document.documentElement
    el.dataset.scrollLockCount = String(parseInt(el.dataset.scrollLockCount || "0") + 1)
    el.classList.add("overflow-hidden")
    document.body.classList.add("overflow-hidden")
    this.holdsScrollLock = true
  }

  unlockScroll() {
    if (!this.holdsScrollLock) return
    const el    = document.documentElement
    const count = Math.max(0, parseInt(el.dataset.scrollLockCount || "0") - 1)
    el.dataset.scrollLockCount = String(count)
    if (count === 0) {
      el.classList.remove("overflow-hidden")
      document.body.classList.remove("overflow-hidden")
    }
    this.holdsScrollLock = false
  }
}
